// Keeps the production GlobalState in step with the funnel's answers, so the production code that reads it keeps
// working unchanged: the interface language (setLanguage → languageCode), `name` / `email` / `language` sent by
// web-onboarding-email when the paywall opens, and level / hows / why / goal / struggles / topics sent by
// finishOnboarding() (web-onboarding-finish) after a purchase. Ids come from src/funnel/productionIds.ts.
//
// The multi-select setters of the production state are toggles that close over the current array, so the sync
// changes one item per render and converges in a few renders (nothing is written to localStorage here —
// the production setters do that themselves).
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useGlobalState } from "../../providers/globalState/GlobalStateProvider";
import { useFunnelState } from "./FunnelState";
import { GOAL_IDS, LANGUAGE_IDS, STRUGGLE_IDS, STUDY_HISTORY_IDS, TOPIC_IDS } from "../productionIds";

const LEVEL_IDS: Record<string, number> = { beginner: 1, intermediate: 2, advanced: 3 }; // DataProvider.levels
const MINUTES_IDS: Record<number, number> = { 5: 1, 15: 2, 30: 3 }; // DataProvider.goals ("MINUTES_5" …)

const ids = (map: Record<string, number>, v: unknown) =>
  Array.isArray(v) ? v.map((x) => map[String(x)]).filter((x): x is number => x !== undefined) : [];

export function ProductionSync() {
  const { answers } = useFunnelState();
  const prod = useGlobalState();
  const { pathname } = useLocation();

  // "other" → English, as production does with its id -1
  const language = typeof answers.language === "string" ? (answers.language === "other" ? LANGUAGE_IDS.en : LANGUAGE_IDS[answers.language]) : undefined;
  const level = typeof answers.selfLevel === "string" ? LEVEL_IDS[answers.selfLevel] : undefined;
  const why = typeof answers.goal === "string" ? GOAL_IDS[answers.goal] : undefined;
  const goal = typeof answers.dailyMinutes === "number" ? MINUTES_IDS[answers.dailyMinutes] : undefined;
  const name = typeof answers.name === "string" ? answers.name.trim() : "";
  const hows = ids(STUDY_HISTORY_IDS, answers.studyHistory);
  const struggles = ids(STRUGGLE_IDS, answers.struggles);
  const topics = ids(TOPIC_IDS, answers.topics);

  useEffect(() => {
    if (language !== undefined && language !== prod.language) prod.setLanguage(language);
  }, [language, prod.language]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // not on /level: the production state clears the level on entering that route and auto-navigates on setting it
    if (level !== undefined && level !== prod.level && pathname !== "/level") prod.setLevel(level);
  }, [level, prod.level, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (why !== undefined && why !== prod.why) prod.setWhy(why);
  }, [why, prod.why]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (goal !== undefined && goal !== prod.goal) prod.setGoal(goal);
  }, [goal, prod.goal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (name && name !== prod.name) prod.setName(name);
  }, [name, prod.name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const extra = prod.hows.find((id) => !hows.includes(id));
    const missing = hows.find((id) => !prod.hows.includes(id));
    if (extra !== undefined) prod.chooseHow(extra);
    else if (missing !== undefined) prod.chooseHow(missing);
  }, [hows.join(","), prod.hows]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const extra = prod.struggles.find((id) => !struggles.includes(id));
    const missing = struggles.find((id) => !prod.struggles.includes(id));
    if (extra !== undefined) prod.chooseStruggle(extra);
    else if (missing !== undefined) prod.chooseStruggle(missing);
  }, [struggles.join(","), prod.struggles]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const extra = prod.topics.find((id) => !topics.includes(id));
    const missing = topics.find((id) => !prod.topics.includes(id));
    if (extra !== undefined) prod.chooseTopic(extra);
    else if (missing !== undefined) prod.chooseTopic(missing);
  }, [topics.join(","), prod.topics]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
